<template>
    <div class="content mk-lp-list">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.landing_pages") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="d-flex flex-wrap align-items-center justify-content-between" style="gap: 12px;">
                        <h3 class="mb-0">{{ $t("marketing.menu.landing_pages") }}</h3>
                        <base-button v-if="canWrite && !schemaMissing" type="primary" size="sm" data-testid="lp-create" @click="openCreate">
                            <i class="fa fa-plus"></i> {{ $t("marketing.lp.create") }}
                        </base-button>
                    </div>
                </template>

                <!-- padded wrapper, not margins on the 100%-wide alert (that overflowed the card by 2 × 1.5rem) -->
                <div v-if="schemaMissing" class="px-4 pb-3">
                    <el-alert type="error" :closable="false" show-icon :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>
                </div>

                <div class="d-flex flex-wrap align-items-center px-4 pb-3" style="gap: 12px;">
                    <el-radio-group v-model="status" size="small" @change="reload(1)">
                        <el-radio-button label="">{{ $t("marketing.lp.tab_all") }}</el-radio-button>
                        <el-radio-button v-for="s in statusTabs" :key="s" :label="s">{{ $t("marketing.lp.state." + s) }}</el-radio-button>
                    </el-radio-group>
                    <el-input v-model="q" size="small" clearable prefix-icon="el-icon-search" :placeholder="$t('marketing.lp.search')" style="max-width: 280px;" @input="onSearch"></el-input>
                </div>

                <el-table v-loading="loading" :data="rows" class="bon-table bon-tight" row-key="id" header-row-class-name="thead-light" :empty-text="$t('marketing.report.empty')">
                    <el-table-column :label="$t('marketing.lp.col.page')" min-width="240">
                        <template v-slot="{ row }">
                            <router-link :to="'/marketing/landing-pages/' + row.id" class="font-weight-bold">{{ row.title }}</router-link>
                            <div class="text-muted small">/lp/{{ row.slug }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.lp.col.state')" width="150">
                        <template v-slot="{ row }">
                            <el-tag size="small" :type="tagType(row.live_state)">{{ $t("marketing.lp.state." + (row.live_state || row.status)) }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.lp.col.window')" min-width="200">
                        <template v-slot="{ row }">
                            <span v-if="!row.starts_at && !row.ends_at" class="text-muted">{{ $t("marketing.lp.always") }}</span>
                            <span v-else class="small">{{ row.starts_at ? fmt(row.starts_at) : "…" }} – {{ row.ends_at ? fmt(row.ends_at) : "…" }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.lp.col.version')" width="120">
                        <template v-slot="{ row }">
                            <span v-if="row.published_version">v{{ row.published_version }}</span>
                            <span v-else class="text-muted">-</span>
                            <el-tooltip v-if="row.has_unpublished_changes && row.published_version" :content="$t('marketing.lp.state.unpublished_changes')" placement="top">
                                <span class="mk-dot" :aria-label="$t('marketing.lp.state.unpublished_changes')"></span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.lp.col.updated')" min-width="170">
                        <template v-slot="{ row }">
                            <div class="small">{{ fmt(row.updated_at) }}</div>
                            <div class="small text-muted">{{ row.updated_by_name }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('marketing.lp.col.actions')" width="90" align="center">
                        <template v-slot="{ row }">
                            <el-dropdown trigger="click" @command="(c) => onAction(c, row)">
                                <base-button size="sm" type="neutral" class="px-2" :aria-label="$t('marketing.lp.col.actions')"><i class="fa fa-ellipsis-v"></i></base-button>
                                <el-dropdown-menu slot="dropdown">
                                    <el-dropdown-item command="edit">{{ $t("marketing.lp.edit") }}</el-dropdown-item>
                                    <el-dropdown-item command="open" :disabled="!row.published_version">{{ $t("marketing.lp.open_page") }}</el-dropdown-item>
                                    <el-dropdown-item command="copy">{{ $t("marketing.lp.copy_ad_link") }}</el-dropdown-item>
                                    <template v-if="canWrite">
                                        <el-dropdown-item command="duplicate" divided>{{ $t("marketing.lp.duplicate") }}</el-dropdown-item>
                                        <el-dropdown-item command="unpublish" :disabled="row.status !== 'published'">{{ $t("marketing.lp.unpublish") }}</el-dropdown-item>
                                        <el-dropdown-item command="archive" :disabled="row.status === 'published' || row.status === 'archived'">{{ $t("marketing.lp.archive") }}</el-dropdown-item>
                                        <el-dropdown-item command="delete" :disabled="row.status === 'published'" class="text-danger">{{ $t("marketing.lp.delete") }}</el-dropdown-item>
                                    </template>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </template>
                    </el-table-column>
                </el-table>

                <div slot="footer" class="d-flex justify-content-end px-3">
                    <el-pagination layout="total, prev, pager, next" :total="total" :page-size="limit" :current-page="page" @current-change="reload"></el-pagination>
                </div>
            </card>
        </div>

        <!-- create / duplicate -->
        <el-dialog :title="dialogMode === 'duplicate' ? $t('marketing.lp.duplicate_title') : $t('marketing.lp.create')" :visible.sync="dialogOpen" width="480px" append-to-body>
            <div class="form-group">
                <label class="form-control-label">{{ $t("marketing.lp.field.title") }}</label>
                <el-input v-model="form.title" maxlength="200" data-testid="lp-new-title"></el-input>
            </div>
            <div class="form-group mb-0">
                <label class="form-control-label">{{ $t("marketing.lp.field.slug") }}</label>
                <el-input v-model="form.slug" maxlength="60" data-testid="lp-new-slug" @input="onSlugInput">
                    <template slot="prepend">/lp/</template>
                </el-input>
                <small class="d-block mt-1" :class="slugState.ok ? 'text-success' : slugState.text ? 'text-danger' : 'text-muted'">
                    {{ slugState.text || $t("marketing.lp.slug_hint") }}
                </small>
            </div>
            <span slot="footer">
                <base-button type="secondary" size="sm" @click="dialogOpen = false">{{ $t("marketing.lp.cancel") }}</base-button>
                <base-button type="primary" size="sm" :loading="saving" :disabled="!canSubmitDialog" data-testid="lp-new-submit" @click="submitDialog">{{ $t("marketing.lp.confirm") }}</base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { Table, TableColumn, Tag, Alert, RadioGroup, RadioButton, Dropdown, DropdownMenu, DropdownItem, Pagination, Dialog, Loading } from "element-ui";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { LandingPageService } from "@/resource";
import { adUrl, apiErrorCode, fmtBkk, isSchemaMissing, liveStateTag, lpUrl, slugProblem } from "@/utils/marketing";
import { storefrontOrigin } from "@/utils/sellerProductRow";
import { copyToClipboard } from "@/utils/clipboard";

// การตลาด → หน้า Landing (โฆษณา) — list (Phase 1 ads spec 2026-10-13 §6.2).
export default {
    name: "MarketingLandingPageList",
    directives: { loading: Loading.directive },
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Pagination.name]: Pagination,
        [Dialog.name]: Dialog
    },
    data() {
        return {
            loading: false,
            schemaMissing: false,
            rows: [],
            total: 0,
            page: 1,
            limit: 20,
            status: "",
            q: "",
            statusTabs: ["live", "scheduled", "ended", "draft", "archived"],
            dialogOpen: false,
            dialogMode: "create",
            dialogSource: null,
            form: { title: "", slug: "" },
            slugState: { ok: false, text: "" },
            saving: false,
            searchTimer: null,
            slugTimer: null
        };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteMarketing" }),
        canSubmitDialog() {
            return !!this.form.title.trim() && this.slugState.ok && !this.saving;
        }
    },
    created() {
        this.reload(1);
    },
    methods: {
        fmt: fmtBkk,
        tagType: liveStateTag,
        async reload(page) {
            if (page) this.page = page;
            this.loading = true;
            try {
                const params = { page: this.page, limit: this.limit };
                if (this.status) params.status = this.status;
                if (this.q.trim()) params.q = this.q.trim();
                const res = await LandingPageService.list(params);
                const body = res.data || {};
                this.rows = body.data || [];
                this.total = body.total || 0;
                this.schemaMissing = false;
            } catch (err) {
                this.rows = [];
                this.total = 0;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.$notify({ type: "danger", message: this.$t("marketing.lp.err.load") });
            } finally {
                this.loading = false;
            }
        },
        onSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.reload(1), 350);
        },
        openCreate() {
            this.dialogMode = "create";
            this.dialogSource = null;
            this.form = { title: "", slug: "" };
            this.slugState = { ok: false, text: "" };
            this.dialogOpen = true;
        },
        openDuplicate(row) {
            this.dialogMode = "duplicate";
            this.dialogSource = row;
            this.form = { title: row.title + " (copy)", slug: (row.slug + "-copy").slice(0, 60) };
            this.dialogOpen = true;
            this.onSlugInput();
        },
        onSlugInput() {
            const slug = this.form.slug.trim();
            const local = slugProblem(slug);
            if (local) {
                this.slugState = { ok: false, text: slug ? this.$t("marketing.lp.slug_status." + local) : "" };
                return;
            }
            this.slugState = { ok: false, text: this.$t("marketing.lp.slug_status.checking") };
            clearTimeout(this.slugTimer);
            this.slugTimer = setTimeout(async () => {
                try {
                    const res = await LandingPageService.checkSlug(slug);
                    const d = (res.data && res.data.data) || {};
                    if (slug !== this.form.slug.trim()) return;
                    this.slugState = d.available
                        ? { ok: true, text: this.$t("marketing.lp.slug_status.available") }
                        : { ok: false, text: this.$t("marketing.lp.slug_status." + (d.reason || "taken")) };
                } catch (e) {
                    // check-slug failing must not block: the POST answers 409 slug_taken anyway.
                    this.slugState = { ok: true, text: "" };
                }
            }, 300);
        },
        async submitDialog() {
            this.saving = true;
            try {
                const payload = { title: this.form.title.trim(), slug: this.form.slug.trim() };
                const res = this.dialogMode === "duplicate"
                    ? await LandingPageService.duplicate(this.dialogSource.id, payload)
                    : await LandingPageService.create(payload);
                const lp = (res.data && res.data.data) || {};
                this.dialogOpen = false;
                this.$notify({ type: "success", message: this.$t(this.dialogMode === "duplicate" ? "marketing.lp.duplicated" : "marketing.lp.created") });
                if (lp.id) this.$router.push("/marketing/landing-pages/" + lp.id);
                else this.reload();
            } catch (err) {
                this.showError(err);
            } finally {
                this.saving = false;
            }
        },
        showError(err) {
            const code = apiErrorCode(err);
            if (code === "slug_taken" || code === "invalid_slug") {
                this.slugState = { ok: false, text: this.$t("marketing.lp.err." + code) };
                return;
            }
            const key = "marketing.lp.err." + code;
            this.$notify({ type: "danger", message: code && this.$te(key) ? this.$t(key) : this.$t("marketing.lp.err.generic") });
        },
        async confirm(textKey, danger) {
            const r = await swal.fire({
                text: this.$t(textKey),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("marketing.lp.confirm"),
                cancelButtonText: this.$t("marketing.lp.cancel"),
                confirmButtonColor: danger ? "#d33" : "#5e72e4",
                cancelButtonColor: "#6c757d"
            });
            return !!r.value;
        },
        async onAction(cmd, row) {
            switch (cmd) {
                case "edit":
                    this.$router.push("/marketing/landing-pages/" + row.id);
                    return;
                case "open":
                    window.open(lpUrl(storefrontOrigin(), row.slug), "_blank", "noopener");
                    return;
                case "copy": {
                    const ok = await copyToClipboard(adUrl(storefrontOrigin(), row.slug));
                    this.$notify({ type: ok ? "success" : "danger", message: ok ? this.$t("marketing.lp.copied") : this.$t("marketing.lp.err.generic") });
                    return;
                }
                case "duplicate":
                    this.openDuplicate(row);
                    return;
                default:
                    break;
            }
            const spec = {
                unpublish: ["marketing.lp.confirm_unpublish", false, "unpublish", "marketing.lp.unpublished"],
                archive: ["marketing.lp.confirm_archive", false, "archive", "marketing.lp.archived_ok"],
                delete: ["marketing.lp.confirm_delete", true, "remove", "marketing.lp.deleted"]
            }[cmd];
            if (!spec || !(await this.confirm(spec[0], spec[1]))) return;
            try {
                await LandingPageService[spec[2]](row.id);
                this.$notify({ type: "success", message: this.$t(spec[3]) });
                this.reload();
            } catch (err) {
                this.showError(err);
            }
        }
    }
};
</script>

<style scoped>
.mk-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-left: 6px;
    border-radius: 50%;
    background: #fb6340;
    vertical-align: middle;
}
</style>
